/** @format */

import { useEffect, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { shipmentService } from "@/services/shipmentService";
import { isQuoteShipment, normalizeShipment } from "@/utils/shipmentUtils";

export const SHIPMENTS_PAGE_SIZE = 10;

/** Defensively pull the shipment array + total out of the various response shapes. */
function unwrapShipmentsPage(page) {
  const body = page ?? {};
  const data = body.data ?? body;
  const shipments =
    data?.shipments ?? body?.shipments ?? (Array.isArray(data) ? data : []) ?? [];
  const total = data?.total ?? body?.total ?? data?.total_count ?? null;
  const hasMore = data?.has_more ?? body?.has_more ?? null;
  return { shipments, total, hasMore };
}

function getNextShipmentOffset(lastPage, allPages) {
  const loaded = allPages.reduce(
    (sum, page) => sum + unwrapShipmentsPage(page).shipments.length,
    0,
  );
  const { shipments, total, hasMore } = unwrapShipmentsPage(lastPage);
  const pageBatch = shipments.length;

  if (hasMore === false) return undefined;
  if (hasMore === true) return loaded;

  if (total != null) return loaded < total ? loaded : undefined;

  // No metadata from the API — assume there is another page only when this one was full.
  return pageBatch >= SHIPMENTS_PAGE_SIZE ? loaded : undefined;
}

export function useInfiniteShipments() {
  const query = useInfiniteQuery({
    queryKey: ["all_shipments", "infinite", SHIPMENTS_PAGE_SIZE],
    queryFn: ({ pageParam = 0 }) =>
      shipmentService.getShipments(null, SHIPMENTS_PAGE_SIZE, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => getNextShipmentOffset(lastPage, allPages),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const shipments = useMemo(() => {
    const raw =
      query.data?.pages?.flatMap((page) => unwrapShipmentsPage(page).shipments) ?? [];
    const seen = new Set();
    const deduped = [];
    for (const shipment of raw) {
      if (isQuoteShipment(shipment)) continue;
      const normalized = normalizeShipment(shipment);
      if (normalized?.id != null && seen.has(normalized.id)) continue;
      if (normalized?.id != null) seen.add(normalized.id);
      deduped.push(normalized);
    }
    return deduped;
  }, [query.data]);

  const rawLoadedCount =
    query.data?.pages?.reduce(
      (sum, page) => sum + unwrapShipmentsPage(page).shipments.length,
      0,
    ) ?? 0;

  // A page of only quotes would otherwise look empty and hide booked shipments
  // on the next page. Keep loading until a real shipment appears or the list ends.
  const isSkippingQuotes =
    shipments.length === 0 && rawLoadedCount > 0 && Boolean(query.hasNextPage) && !query.isError;

  useEffect(() => {
    if (!isSkippingQuotes || query.isLoading || query.isFetchingNextPage) return;
    query.fetchNextPage();
  }, [isSkippingQuotes, query.isLoading, query.isFetchingNextPage, query.fetchNextPage]);

  const total =
    query.data?.pages?.[0] != null
      ? unwrapShipmentsPage(query.data.pages[0]).total ?? shipments.length
      : 0;

  return {
    shipments,
    total,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
    isLoading: query.isLoading || isSkippingQuotes || (query.isFetchingNextPage && shipments.length === 0),
    isFetchingMore: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
